<template>
  <div>
    <!-- Toast de alerta -->
    <ToastAlert
      :is-active="toastAlert"
      :type="typeMsg"
      :message="msg"
      :title="tittle"
      @close="closeToast"
    />
    <div class="flex max-w-[1440px] mx-auto h-full font-poppins bg-white">
      <!-- Panel izquierdo -->
      <div class="w-full sm:w-1/2 flex flex-col bg-white min-h-screen">
        <!-- Encabezado -->
        <HeaderComponent class="md:mb-6 mb-4" />

        <!-- Contenido -->
        <div class="mb-4 mx-auto px-5 md:p-0 md:ml-[80px] md:mx-0 w-11/12 md:w-3/4 max-w-[384px] bg-white">
          <!-- Barra de progreso -->
          <div class="flex justify-between mb-0">
            <span></span>
            <span class="text-sm font-medium text-custom_gray">{{ currentStep + 1 }}/4</span>
          </div>
          <div class="md:mb-6 mb-3 w-full bg-[#D9D9D9] rounded-full h-2.5">
            <div class="bg-tuin-100 h-2.5 rounded-full" :style="{ width: progressPercentage + '%' }"></div>
          </div>

          <!-- Título -->
          <h5 class="text-[26px] md:text-[30px] font-medium text-custom_black mb-2">
            ¡Ya casi terminamos!
          </h5>

          <!-- Mensaje solo en Desktop -->
          <div class="hidden md:block">
            <p class="text-custom_gray font-normal text-sm lg:text-base not-italic">
              Enviamos un código por WhatsApp al número
            </p>
            <p class="text-custom_black2 font-semibold text-base md:text-xl not-italic mb-1">
              {{ selectedCountryCode }} {{ formatPhoneNumber(userPhoneNumber) }}
            </p>
          </div>

          <!-- Mensaje solo en Mobile -->
          <div class="block md:hidden">
            <p class="text-custom_gray font-normal text-sm lg:text-base not-italic text-center">
              Enviamos un código por WhatsApp al número
              <span class="text-custom_black2 font-semibold text-base lg:text-xl not-italic">
                {{ selectedCountryCode }} {{ formatPhoneNumber(userPhoneNumber) }}
              </span>
            </p>
          </div>

          <!-- Tiempo restante -->
          <p class="text-custom_black2 font-semibold text-xl lg:text-[22px] not-italic text-center md:text-start">
            {{ formatTime(timeRemaining) }}
          </p>

          <!-- Mensaje de error -->
          <p v-if="error" class="text-red-500 text-sm font-medium mt-1">{{ error }}</p>

          <!-- Reenvío del código -->
          <div 
            class="flex flex-row items-center justify-center md:justify-start text-[12px] md:text-sm font-medium text-custom_gray"
            v-if="timeRemaining === 0"
          >
            <span>Si el código no llegó,&nbsp;</span>
            <a href="#" @click.prevent="resendCode" class="text-tuin_base-0 font-semibold hover:underline cursor-pointer">
              envíalo de nuevo
            </a>
          </div>

          <!-- Campo para ingresar el código -->
          <div class="flex flex-col items-center justify-center space-y-4 md:space-y-6 md:items-start md:justify-start">
            <div>
              <label for="email" class="block mb-2 text-sm font-medium text-custom_gray">
                Introduce el código de 6 dígitos que recibiste
              </label>

              <!-- Inputs del código -->
              <div class="flex justify-center md:justify-start">
                <div class="flex mb-2 space-x-2 rtl:space-x-reverse">
                  <div v-for="n in 6" :key="n">
                    <label :for="'code-' + n" class="sr-only">Código {{ n }}</label>
                    <input
                      type="text"
                      maxlength="1"
                      :id="'code-' + n"
                      v-model="digits[n-1]"
                      class="block w-9 h-9 py-3 text-sm font-light text-center text-custom_gray bg-white border border-gray-300 rounded-lg focus:ring-tuin-100 focus:border-tuin-100"
                      required
                      @input="onInputChange($event, n)"
                      :data-focus-input-prev="n > 1 ? 'code-' + (n - 1) : null"
                      :data-focus-input-next="n < 5 ? 'code-' + (n + 1) : null"
                      @paste="onPasteCode"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Botón verificar -->
          <button 
            @click="verifyOTP"
            class="w-full bg-tuin-100 text-white py-2 px-4 rounded-lg mt-4 font-medium hover:bg-tuin_hover focus:outline-none focus:ring-2 focus:ring-tuin-100"
            :disabled="isSubmitting"
          >
            {{ isSubmitting ? 'Verificar' : 'Verificar' }}
          </button>

          <div class="flex flex-col md:flex-row font-normal text-sm pt-7 md:pt-4 text-center">
            <span class="text-custom_gray">Si este no es tu número,&nbsp;</span>
            <a class="text-tuin_base-0 hover:underline md:ml-1 cursor-pointer" @click="editPhone">
              Haz Clic Aquí Para Editarlo
            </a>
          </div>
        </div>
      </div>

      <!-- Panel derecho con imagen -->
      <div class="hidden w-1/2 sm:flex items-center justify-center">
        <div>
          <img
            src="@/assets/imgs/undraw_small-town_76a2.svg"
            alt="Tuin hora de hacer magia"
            class="max-h-[353px] max-w-[717px]"
          >
        </div>
      </div>
    </div> <!-- Cierre del div raíz -->
  </div>
  
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import axios from 'axios';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import HeaderComponent from '@/components/login/HeaderComponent.vue';
import ToastAlert from '@/commond/ToastAlert.vue'

// Store, router y session
const store = useStore();
const router = useRouter();
const route = useRoute();
const session = computed(() => store.getters.getUserSession);

// Variables
const digits = ref(Array(6).fill(''));
const currentStep = ref(2);
const progressPercentage = computed(() => ((currentStep.value + 1) / 4) * 100);
const timeRemaining = ref(300); // 5 minutos en segundos
const error = ref('');
const step = ref('');
const selectedCountryCode = ref('');
const userPhoneNumber = ref('');
const isSubmitting = ref(false);
const userProfile = ref(null);
let timerInterval = null;

// Estado para alertas
const toastAlert = ref(false)
const typeMsg = ref('')
const msg = ref('')
const tittle = ref('')

// Función para formatear el tiempo en MM:SS
const formatTime = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// Función para formatear el número de teléfono como xxx-xxx-xxxx
const formatPhoneNumber = (phoneNumber) => {
  // Eliminar cualquier carácter que no sea número
  const cleaned = ('' + phoneNumber).replace(/\D/g, '');
  
  // Verificar si el número tiene la longitud adecuada
  if (cleaned.length === 10) {
    return cleaned.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
  } else if (cleaned.length > 10) {
    // Si tiene más de 10 dígitos, formatear los últimos 10
    const lastTen = cleaned.slice(-10);
    return lastTen.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
  }
  
  // Si no tiene 10 dígitos, devolver el número sin formato
  return phoneNumber;
};

// Función para validar solo números
const onInputChange = (event, index) => {
  // Asegurarse de que solo se ingresen números
  event.target.value = event.target.value.replace(/[^0-9]/g, '');
  
  // Si se ingresó un dígito, mover al siguiente input
  if (event.target.value && index < 6) {
    const nextInput = document.getElementById(`code-${index + 1}`);
    if (nextInput) nextInput.focus();
  }
  
  // Si se borró un dígito, mover al input anterior
  if (event.target.value === '' && index > 1) {
    const prevInput = document.getElementById(`code-${index - 1}`);
    if (prevInput) prevInput.focus();
  }
};

// Iniciar el temporizador cuando se monta el componente
onMounted(async () => {
  // Intentar obtener el número de teléfono de la ruta primero
  if (route.query.phone) {
    userPhoneNumber.value = route.query.phone;
    
    // Intentar obtener el código de país de la ruta
    if (route.query.countryCode) {
      selectedCountryCode.value = route.query.countryCode;
    } else {
      // Si no está en la ruta, obtenerlo del perfil del usuario
      await getProfile();
    }
  } else {
    // Si no está en la ruta, obtenerlo del perfil del usuario
    await getProfile();
  }
  
  startTimer();
});

// Limpiar el temporizador cuando se desmonta el componente
onUnmounted(() => {
  clearInterval(timerInterval);
});

// Obtener el perfil del usuario para conseguir su número de teléfono
async function getProfile() {
  if (!session.value || !session.value.token_api || !session.value.id) {
    error.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    return;
  }
  
  try {
    let options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };
    
    const response = await axios.get(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/${session.value.id}`,
      options
    );
    
    if (response.status == 200) {
      userProfile.value = response.data.resp;
      
      // Extraer el código de país y el número de teléfono directamente de la respuesta
      if (userProfile.value) {
        step.value= userProfile.value.info.step;
        // Usar el campo phone_country_code directamente
        selectedCountryCode.value = userProfile.value.phone_country_code || '+52'; // Valor predeterminado si no existe
        
        // Obtener el número de teléfono del campo phone
        if (userProfile.value.info && userProfile.value.info.phone) {
          userPhoneNumber.value = userProfile.value.info.phone;
        } else {
          userPhoneNumber.value = '';
        }
      } else {
        // Valores predeterminados si no hay datos
        selectedCountryCode.value = '+52';
        userPhoneNumber.value = '';
      }
    }
  } catch (error) {
    let response = error.response;
    if (response && response.status == 401) {
      store.dispatch("clearUserSession");
      router.push({ name: 'login' });
    }
    console.error("Error fetching profile data:", error.response);
  }
}

// Iniciar la cuenta regresiva
const startTimer = () => {
  // Limpiar cualquier timer existente antes de crear uno nuevo
  if (timerInterval) {
    clearInterval(timerInterval);
  }
  
  timerInterval = setInterval(() => {
    if (timeRemaining.value > 0) {
      timeRemaining.value--;
    } else {
      clearInterval(timerInterval);
      timerInterval = null;
    }
  }, 1000);
};

// Función para enviar el código OTP
const sendOTP = async () => {
  // Verificar si la sesión contiene los datos necesarios
  if (!session.value || !session.value.token_api || !session.value.id) {
    isSubmitting.value = false; // Detener el estado de envío
    error.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    return;
  }
  
  // Obtener token y userId de la sesión
  const token = session.value.token_api;
  const userId = session.value.id;
  
  const url = `${process.env.VUE_APP_API_BASE_URL}/send-otp`;
  
  const options = {
    headers: {
      Authorization: `Bearer ${token.trim()}`,
    },
  };
  
  try {
    // Enviar la solicitud para OTP
    const response = await axios.post(
      url,
      {
        user_id: userId,
        phone_number: selectedCountryCode.value + userPhoneNumber.value, // Concatenar código de país con número de teléfono
      },
      options
    );
    
    isSubmitting.value = false; // Detener el estado de envío
    
    if (response.status === 200) {
      // Reiniciar el temporizador
      timeRemaining.value = 300;
      startTimer();
      
      // Limpiar cualquier mensaje de error previo
      error.value = "";
      
      return true;
    }
    
    return false;
  } catch (err) {
    isSubmitting.value = false; // Detener el estado de envío
    console.error("Error al enviar código OTP:", err);
    error.value = err.response?.data?.detail || "Error al enviar el código de verificación.";
    return false;
  }
};

// Función para reenviar el código
const resendCode = async () => {
  error.value = '';
  isSubmitting.value = true;
  
  const result = await sendOTP();
  
  if (result) {
    // Limpiar los campos de código
    digits.value = Array(6).fill('');
  }
  
  isSubmitting.value = false;
};

const editPhone = async () => {
  const isStep3Verification = route.query.isStep3 === 'true';
      
  if (isStep3Verification) {
    setTimeout(() => {
      closeToast()
      router.push({ name: 'info_profile' }) // Redirige a la ruta 'step3'
    }, 2000)
  }else{
    await updateStep(1);
    // Redirigir al siguiente paso
    setTimeout(() => {
      closeToast()
      router.push({ name: 'step1' }) // Redirige a la ruta 'step3'
    }, 2000)
  }
};

// Agregar función para guardar solo el teléfono
const savePhoneOnly = async () => {
  if (!session.value || !session.value.token_api || !session.value.id) {
    console.error("No se encontró la sesión del usuario");
    return false;
  }

  try {
    const phoneData = {
      phone: userPhoneNumber.value.replace(/-/g, ""),
      mobile_phone: selectedCountryCode.value + userPhoneNumber.value.replace(/-/g, ""),
      country_code: selectedCountryCode.value
    };

    const options = {
      headers: {
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };

    await axios.put(
      `${process.env.VUE_APP_API_BASE_URL}/broker/profile/${session.value.id}/update-phone`,
      phoneData,
      options
    );

    return true;
  } catch (error) {
    console.error("Error al guardar el teléfono:", error);
    return false;
  }
};
// Función para verificar el código OTP
const verifyOTP = async () => {
  if (isSubmitting.value) return;
  error.value = '';
  isSubmitting.value = true;
  
  if (timeRemaining.value <= 0) {
    error.value = "El código ha expirado. Por favor, solicita un nuevo código.";
    isSubmitting.value = false;
    return;
  }
  
  const code = digits.value.join('');
  
  if (code.length !== 6) {
    error.value = "Por favor, ingresa el código completo de 6 dígitos.";
    isSubmitting.value = false;
    return;
  }
  
  if (!session.value || !session.value.token_api || !session.value.id) {
    error.value = "No se encontró la sesión del usuario. Vuelve a iniciar sesión.";
    isSubmitting.value = false;
    return;
  }
  
  const token = session.value.token_api;
  const userId = session.value.id;
  
  try {
    const url = `${process.env.VUE_APP_API_BASE_URL}/verify-otp`;
    
    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    };
    
    const response = await axios.post(url, { user_id: userId, code }, options);
    
    if (response.status === 200) {
      // Si es step 3 (verificación desde perfil), guardar el teléfono
      const isStep3Verification = route.query.isStep3 === 'true';
      
      if (isStep3Verification) {
        const phonesSaved = await savePhoneOnly();
        if (!phonesSaved) {
          error.value = "Error al guardar el teléfono verificado";
          isSubmitting.value = false;
          return;
        }
      }
      
      toastAlert.value = true;
      typeMsg.value = 'success';
      msg.value = 'Todo listo. Tu número ha sido verificado correctamente.';
      tittle.value = 'Verificación exitosa';
      
      if (step.value == 3 || isStep3Verification) {
        setTimeout(() => {
          closeToast();
          router.push({ name: 'info_profile' });
        }, 2000);
      } else {
        // Flujo normal de registro
        await goToProperties();
        currentStep.value++;
        await updateStep(3);
        
        setTimeout(() => {
          closeToast();
          router.push({ name: 'step3' });
        }, 2000);
      }
    }
  } catch (err) {
    error.value = err.response?.data?.message || "Código incorrecto o expirado";
    toastAlert.value = true;
    typeMsg.value = 'danger';
    msg.value = 'Código incorrecto. Asegúrate de ingresarlo correctamente o solicita uno nuevo.';
    tittle.value = 'Error';
    setTimeout(() => closeToast(), 5000);
  } finally {
    isSubmitting.value = false;
  }
};

// Modificar onMounted para obtener datos de la query si viene de step 3
onMounted(async () => {
  // Si viene de step 3 con datos en la query, usar esos datos
  if (route.query.phone && route.query.countryCode) {
    userPhoneNumber.value = route.query.phone;
    selectedCountryCode.value = route.query.countryCode;
  } else {
    // Si no, obtener del perfil del usuario
    await getProfile();
  }
  
  startTimer();
});

// Función para finalizar el registro
async function goToProperties() {
  if (!session.value || !session.value.id) {
    console.error("No se encontró el ID de usuario.");
    return;
  }

  try {
    // Enviar solicitud para marcar el registro como finalizado
    await axios.post(`${process.env.VUE_APP_API_BASE_URL}/finish-register`, {
      user_id: session.value.id,
    });

    console.log("Registro completado exitosamente.");
  } catch (error) {
    console.error("Error al finalizar el registro:", error.response || error);
  }
}

const updateStep = async (stepNumber) => {
  try {
    const url = `${process.env.VUE_APP_API_BASE_URL}/profile/save-step/${session.value.id}`;
    
    const options = {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.value.token_api}`,
      },
    };

    const response = await axios.post(url, { step: stepNumber }, options);
    
    if (response.status === 200) {
      console.log('Paso actualizado correctamente');
    }
  } catch (err) {
    console.error('Error actualizando el paso:', err.response?.data || err);
    error.value = err.response?.data?.message || "Error al actualizar el paso";
  }
};
// Emitir el código OTP cuando cambie
watch(digits, (newDigits) => {
  const fullCode = newDigits.join('');
  // Si se completaron los 5 dígitos, verificar automáticamente
  if (fullCode.length === 6) {
    verifyOTP();
  }
});

async function closeToast() {
  toastAlert.value = false;
  typeMsg.value = "";
  msg.value = "";
  tittle.value = "";
}

// Función para pegar el código OTP en los inputs
const onPasteCode = (event) => {
  event.preventDefault();
  const paste = (event.clipboardData || window.clipboardData).getData('text');
  const numbers = paste.replace(/\D/g, '').slice(0, 6).split('');
  numbers.forEach((num, idx) => {
    digits.value[idx] = num;
  });
  // Opcional: enfocar el último input si se pegaron 6 dígitos
  if (numbers.length === 6) {
    const lastInput = document.getElementById('code-6');
    if (lastInput) lastInput.focus();
  }
};

</script>

<style scoped>
/* Puedes agregar estilos específicos para este componente si lo necesitas */
</style>